/**
 * Great-circle geometry for a satellite's ground coverage — the horizon disk it
 * can see from altitude. groundRadius = R·acos(R/(R+alt)); the ring is sampled
 * as great-circle destination points around the sub-satellite point.
 */

/** Mean Earth radius (km). */
const EARTH_RADIUS_KM = 6371;

/** Degrees ↔ radians. */
const DEG = Math.PI / 180;

/** Default ring resolution. */
const DEFAULT_SEGMENTS = 72;

/** Ground ring (closed) of the satellite's visible horizon at `altitudeKm`. */
export function computeHorizonFootprint(
  lat: number,
  lon: number,
  altitudeKm: number,
  segments: number = DEFAULT_SEGMENTS,
): google.maps.LatLngLiteral[] {
  const angular = horizonAngularRadius(altitudeKm);
  const ring: google.maps.LatLngLiteral[] = [];
  for (let i = 0; i <= segments; i += 1) {
    ring.push(ringPoint(lat * DEG, lon * DEG, angular, (2 * Math.PI * i) / segments));
  }
  return ring;
}

/** Angular radius (radians) from the sub-point to the visible horizon. */
function horizonAngularRadius(altitudeKm: number): number {
  const ratio = EARTH_RADIUS_KM / (EARTH_RADIUS_KM + Math.max(altitudeKm, 0));
  // Clamp to [-1, 1] to guard against floating-point drift before acos().
  return Math.acos(Math.min(1, Math.max(-1, ratio)));
}

/** Ground radius (km) of the visible horizon disk seen from `altitudeKm`. */
export function groundRadiusKm(altitudeKm: number): number {
  return EARTH_RADIUS_KM * horizonAngularRadius(altitudeKm);
}

/** Great-circle destination point at an angular distance along a bearing. */
function ringPoint(
  latRad: number,
  lonRad: number,
  angular: number,
  bearing: number,
): google.maps.LatLngLiteral {
  const sinLat = Math.sin(latRad) * Math.cos(angular) + Math.cos(latRad) * Math.sin(angular) * Math.cos(bearing);
  const destLat = Math.asin(Math.min(1, Math.max(-1, sinLat)));
  const y = Math.sin(bearing) * Math.sin(angular) * Math.cos(latRad);
  const x = Math.cos(angular) - Math.sin(latRad) * sinLat;
  const destLon = lonRad + Math.atan2(y, x);
  return { lat: destLat / DEG, lng: normalizeLon(destLon / DEG) };
}

/** Wrap a longitude to [-180, 180]. */
function normalizeLon(lon: number): number {
  return ((((lon + 180) % 360) + 360) % 360) - 180;
}
